import React from "react";
import {
  Col,
  Card,
  CardBody,
  FormGroup,
  Input,
  Label,
  Button,
} from "reactstrap";
import { Controller } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { useDataFetch } from "../../Hooks/Use-data-fetch";
import Dropzone from "react-dropzone-uploader";
import "react-dropzone-uploader/dist/styles.css";
import { toast } from "react-toastify";

// Función para convertir archivo a base64
const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.split(",")[1]); // extraigo solo base64
    reader.onerror = (error) => reject(error);
  });

export const CountryForm = ({
  control,
  errors,
  trigger,
  isSubmitting,
  getValues,
  setValue,
  editMode,
  cancelEdit,
}) => {
  const paymentGateways = ["wompi", "stripe", "coinpayment","monnet"];

  const currencies = [
    { code: "COP", name: "Peso Colombiano" },
    { code: "USD", name: "Dólar Estadounidense" },
    { code: "EUR", name: "Euro" },
  ];

  const { getData, postData } = useDataFetch();

  const { data, isLoading } = getData(
    ["countries"],
    "/product/all/countries",
    {
      enabled: true,
    }
  );

  // Función para subir archivo y actualizar formulario
  const { mutateAsync: uploadFile } = postData(
    "/product/countries-available/upload-file",
    {},
    {
      onSuccess: (response, variables) => {
        const { fieldName } = variables;
        if (response.message === "file_create_success") {        
          setValue(fieldName, response.data.fileName);
          toast.success("Archivo subido exitosamente!");
          trigger(fieldName);
        } else {
          toast.error("Error al subir el archivo");
        }
      },
      onError: () => {
        toast.error("Error al subir el archivo");
      },
    }
  );

  const renderCurrencyInput = (field, name) => (
    <NumericFormat
      {...field}
      className="form-control"
      thousandSeparator="."
      decimalSeparator=","
      prefix="$ "
      decimalScale={2}
      fixedDecimalScale={true}
      allowNegative={false}
      onValueChange={(values) => field.onChange(values.value)}
      onBlur={() => trigger(name)}
    />
  );

  const handleChangeStatus = (fieldName) => async (fileWithMeta, status) => {
    const file = fileWithMeta.file;
    if (status === "done") {
      try {
        const base64URL = await fileToBase64(file);

        await uploadFile({
          file: {
            name: file.name,
            base64URL,
          },
          fieldName,
        });

      } catch (error) {
        toast.error("Error subiendo archivo");
      }
    }

    if (status === "removed") {
      setValue(fieldName, null);
      trigger(fieldName);
    }
  };

  return (
    <Col xxl="12">
      <Card className="h-100">
        <CardBody style={{ maxHeight: "600px", overflowY: "auto" }}>
          {/* Selector de país */}
          <FormGroup>
            <Label className="fw-bold">Nombre del país</Label>
            <Controller
              value={getValues("country_id")}
              name="country_id"
              control={control}
              rules={{ required: "El nombre del país es obligatorio" }}
              render={({ field }) => (
                <Input
                  type="select"
                  {...field}
                  onChange={(e) => {
                    const selected = data?.data?.countries.find(
                      (c) => c.id === e.target.value
                    );
                    if (selected) {
                      setValue("name", selected.name);
                      setValue("country_id", selected.id);
                    }
                  }}
                >
                  <option value="">Selecciona el nombre del país</option>
                  {isLoading ? (
                    <option>Cargando...</option>
                  ) : (
                    data?.data?.countries.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))
                  )}
                </Input>
              )}
            />
            {errors.name && (
              <span className="text-danger small">{errors.name.message}</span>
            )}
          </FormGroup>

          {/* Pasarelas de pago */}
          <FormGroup>
            <Label className="fw-bold">Pasarelas de pago</Label>
            <Controller
              name="paymentGateways"
              control={control}
              render={({ field }) => (
                <div className="m-t-15 m-checkbox-inline custom-radio-ml">
                  {paymentGateways.map((gateway) => (
                    <div className="checkbox checkbox-dark" key={gateway}>
                      <Input
                        id={`gateway-${gateway}`}
                        type="checkbox"
                        value={gateway}
                        checked={field.value.includes(gateway)}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          if (isChecked) {
                            field.onChange([...field.value, gateway]);
                          } else {
                            field.onChange(
                              field.value.filter((g) => g !== gateway)
                            );
                          }
                        }}
                      />
                      <Label htmlFor={`gateway-${gateway}`}>
                        {gateway.charAt(0).toUpperCase() + gateway.slice(1)}
                      </Label>
                    </div>
                  ))}
                </div>
              )}
            />
            {errors.paymentGateways && (
              <span className="text-danger small">
                {errors.paymentGateways.message}
              </span>
            )}
          </FormGroup>

          {/* Moneda local */}
          <FormGroup>
            <Label className="fw-bold">Moneda local</Label>
            <Controller
              name="currency"
              control={control}
              rules={{ required: "La moneda local es obligatoria" }}
              render={({ field }) => (
                <Input
                  type="select"
                  {...field}
                  onBlur={() => trigger("currency")}
                >
                  <option value="">Selecciona una moneda</option>
                  {currencies.map((cur) => (
                    <option key={cur.code} value={cur.code}>
                      {cur.name} ({cur.code})
                    </option>
                  ))}
                </Input>
              )}
            />
            {errors.currency && (
              <span className="text-danger small">{errors.currency.message}</span>
            )}
          </FormGroup>

          {/* Valor de puntos */}
          <FormGroup>
            <Label className="fw-bold">Valor Puntos</Label>
            <Controller
              name="pointValue"
              control={control}
              rules={{ required: "El valor de puntos es obligatorio" }}
              render={({ field }) => renderCurrencyInput(field, "pointValue")}
            />
            {errors.pointValue && (
              <span className="text-danger small">{errors.pointValue.message}</span>
            )}
          </FormGroup>

          {/* Archivos */}

          <FormGroup>
            <Label className="fw-bold">Registro Sanitario (PDF)</Label>
            <Controller
              name="sanitaryRegistration"
              control={control}
              rules={{ required: "Registro sanitario obligatorio" }}
              render={() => (
                <Dropzone
                  onChangeStatus={handleChangeStatus("sanitaryRegistration")}
                  accept="application/pdf"
                  maxFiles={1}
                  inputContent="Arrastra o haz clic para subir el archivo"
                  styles={{
                    dropzone: { minHeight: 80, maxHeight: 120 },
                    dropzoneActive: { borderColor: "green" },
                  }}
                  multiple={false}
                />
              )}
            />
            {errors.sanitaryRegistration && (
              <span className="text-danger small">
                {errors.sanitaryRegistration.message}
              </span>
            )}
          </FormGroup>

          <FormGroup>
            <Label className="fw-bold">Autorización Datos Personales (PDF)</Label>
            <Controller
              name="personalDataAuthorization"
              control={control}
              rules={{ required: "Autorización de datos obligatoria" }}
              render={() => (
                <Dropzone
                  onChangeStatus={handleChangeStatus("personalDataAuthorization")}
                  accept="application/pdf"
                  maxFiles={1}
                  inputContent="Arrastra o haz clic para subir el archivo"
                  styles={{
                    dropzone: { minHeight: 80, maxHeight: 120 },
                    dropzoneActive: { borderColor: "green" },
                  }}
                  multiple={false}
                />
              )}
            />
            {errors.personalDataAuthorization && (
              <span className="text-danger small">
                {errors.personalDataAuthorization.message}
              </span>
            )}
          </FormGroup>

          <FormGroup>
            <Label className="fw-bold">Términos y Condiciones (PDF)</Label>
            <Controller
              name="termsAndConditions"
              control={control}
              rules={{ required: "Términos y condiciones obligatorios" }}
              render={() => (
                <Dropzone
                  onChangeStatus={handleChangeStatus("termsAndConditions")}
                  accept="application/pdf"
                  maxFiles={1}
                  inputContent="Arrastra o haz clic para subir el archivo"
                  styles={{
                    dropzone: { minHeight: 80, maxHeight: 120 },
                    dropzoneActive: { borderColor: "green" },
                  }}
                  multiple={false}
                />
              )}
            />
            {errors.termsAndConditions && (
              <span className="text-danger small">
                {errors.termsAndConditions.message}
              </span>
            )}
          </FormGroup>

          {/* Botones */}
          <div className="d-flex justify-content-end mt-3">
            <Button
              color={editMode ? "warning" : "primary"}
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? editMode
                  ? "Actualizando..."
                  : "Registrando..."
                : editMode
                ? "Actualizar"
                : "Registrar"}
            </Button>

            {editMode && (
              <Button
                color="secondary"
                type="button"
                onClick={cancelEdit}
                className="ms-2"
              >
                Cancelar
              </Button>
            )}
          </div>
        </CardBody>
      </Card>
    </Col>
  );
};
